/**
 * O MAPA ÚNICO de destinos: tipo → caminho, chave, módulo e valores (DS-533, emendada pela
 * DS-638).
 *
 * Havia dois mapas, um na SPA (`link-da-entidade-regras.ts`) e outro que a API precisaria para
 * não avisar quem não abre o destino (6.8), e eles já discordavam no `conta_pagar`: a ficha da
 * 6.4 exige valores no servidor, e o link da SPA não. Os dados moram aqui, e os dois lados os
 * leem: a SPA para decidir se o nome vira link, a API (a porta das notificações) para decidir
 * quem é avisado e com que link.
 *
 * Duas famílias:
 *  - **fichas**: o registro abre a própria tela pela chave (os 16 tipos da DS-533);
 *  - **filas**: o aviso fala de uma superfície (as parcelas a aprovar, a fila de faturamento,
 *    a cobrança), e o clique leva a ela. A fila de faturamento aceita o recorte por pedido.
 */
import { alcanca, veCarteiraDeTodos, veValoresFinanceiros, type Modulo, type NivelDeAcesso } from '@dv/domain'
import type { TipoDeFicha } from './referencias.js'

/** As fichas que um nome pode abrir: as do contrato de referência, mais a conta contábil. */
export type TipoDeFichaDoDestino = TipoDeFicha | 'conta_contabil'

/** Onde a ficha lê a chave: um parâmetro de busca, ou a âncora (`#c-<código>`). */
export type ChaveDaFicha = 'id' | 'v' | 'nf' | 'hash'

export interface DestinoDeFicha {
  readonly caminho: string
  readonly chave: ChaveDaFicha
  /** O módulo que o `@Exige` da rota de detalhe cobra com `ver`. */
  readonly modulo: Modulo
  /** A ficha exige ver valores no servidor (o `conta_pagar`, DS-638). */
  readonly valores?: true
  /** Parâmetro fixo que acompanha a chave (a 10.1 escolhe a categoria por `?cat=`). */
  readonly fixo?: { readonly cat: string }
}

/**
 * O mapa das fichas. O módulo de cada linha é o do `@Exige` da rota de detalhe, conferido um
 * a um (spec 09 §1): mostrar link para uma ficha que o servidor vai recusar é o link morto que
 * a D-136 proíbe.
 */
export const DESTINOS_DE_FICHA = {
  empresa: { caminho: '/empresas', chave: 'id', modulo: 'crm' },
  contato: { caminho: '/contatos', chave: 'id', modulo: 'crm' },
  negocio: { caminho: '/negocios', chave: 'id', modulo: 'negocios' },
  pedido: { caminho: '/pedidos', chave: 'id', modulo: 'pedidos' },
  campanha: { caminho: '/campanhas', chave: 'id', modulo: 'midia' },
  // O `?id=` do fornecedor é o uuid da CAPACIDADE, a mesma chave que a 6.6 resolve.
  fornecedor: { caminho: '/fornecedores', chave: 'id', modulo: 'fornecedores' },
  // A ficha da 6.4 recusa quem não vê valores (`contas-pagar.controller.ts`): o link também.
  conta_pagar: { caminho: '/contas-a-pagar', chave: 'id', modulo: 'contas_a_pagar', valores: true },
  colaborador: { caminho: '/rh', chave: 'id', modulo: 'rh' },
  tarefa: { caminho: '/tarefas', chave: 'id', modulo: 'tarefas' },
  // A 8.2 abre a versão por `?v=` (`orcamento.tsx`); `?id=` abria a lista.
  orcamento_versao: { caminho: '/orcamento', chave: 'v', modulo: 'orcamento' },
  // O Livro de NFs aponta a nota por `?nf=` (`faturamento.tsx`).
  nf: { caminho: '/faturamento', chave: 'nf', modulo: 'faturamento' },
  regra_comissao: { caminho: '/configuracoes/regras-comissao', chave: 'id', modulo: 'comissoes' },
  entidade_dv: { caminho: '/configuracoes/entidades', chave: 'id', modulo: 'entidades_dv' },
  conta_bancaria: { caminho: '/contas-bancarias', chave: 'id', modulo: 'tesouraria' },
  portal_externo: { caminho: '/configuracoes', chave: 'id', modulo: 'usuarios_permissoes', fixo: { cat: 'portais-externos' } },
  /*
    A âncora `#c-…` do Plano de Contas aceita o id da conta, o código ou `g:<grupos>` (DS-604).
    O link novo leva o id, que a renumeração não move.
  */
  conta_contabil: { caminho: '/plano-de-contas', chave: 'hash', modulo: 'contabil_dre' },
} as const satisfies Readonly<Record<TipoDeFichaDoDestino, DestinoDeFicha>>

export interface DestinoDeFila {
  readonly caminho: string
  readonly modulo: Modulo
  readonly valores?: true
  /** A fila que aceita recorte pelo id gravado: a de faturamento recorta por `?pedido=`. */
  readonly chave?: 'pedido'
}

/**
 * Os destinos de FILA: o aviso fala de uma superfície, e o clique leva a ela. A NF está nas
 * duas famílias: com id, abre a própria nota (a ficha); sem id, cai no Livro de NFs.
 */
export const DESTINOS_DE_FILA = {
  recebiveis_cobranca: { caminho: '/recebiveis', modulo: 'recebiveis' },
  fila_faturamento: { caminho: '/faturamento', modulo: 'faturamento', chave: 'pedido' },
  nf: { caminho: '/faturamento', modulo: 'faturamento' },
  comissoes: { caminho: '/comissoes', modulo: 'comissoes' },
  // A fila 6.5 exige `ver` E valores (`aprovacoes.controller.ts`).
  aprovacoes: { caminho: '/aprovacoes', modulo: 'aprovacao_desembolso', valores: true },
  parcela_pagar: { caminho: '/aprovacoes', modulo: 'aprovacao_desembolso', valores: true },
  parcela_comissao: { caminho: '/aprovacoes', modulo: 'aprovacao_desembolso', valores: true },
  pagamento_rh: { caminho: '/aprovacoes', modulo: 'aprovacao_desembolso', valores: true },
  conciliacao: { caminho: '/conciliacao', modulo: 'conciliacao' },
  // D-209: o bloco "Aguardando autorização para faturar" da 4.5, recortado por `?pedido=`. Exige o módulo próprio com
  // valores, que é a célula habilitada (a 0213 não deixa outra): quem recebe o aviso abre e resolve.
  autorizacao_faturamento: { caminho: '/faturamento', modulo: 'autorizacao_faturamento', valores: true, chave: 'pedido' },
} as const satisfies Readonly<Record<string, DestinoDeFila>>

export type TipoDeDestinoDeFila = keyof typeof DESTINOS_DE_FILA

/** O destino de um aviso: o tipo gravado na inbox, o id e, para a ficha de carteira, o dono. */
export interface DestinoDoAviso {
  readonly tipo: TipoDeFichaDoDestino | TipoDeDestinoDeFila
  readonly id: string | null
  /**
   * O dono do registro, quando a ficha recorta por carteira (o negócio, `negocios.controller`):
   * quem não vê a carteira inteira só abre o que é seu.
   */
  readonly donoId?: string | null
}

function ehFicha(tipo: string): tipo is TipoDeFichaDoDestino {
  return Object.hasOwn(DESTINOS_DE_FICHA, tipo)
}

function ehFila(tipo: string): tipo is TipoDeDestinoDeFila {
  return Object.hasOwn(DESTINOS_DE_FILA, tipo)
}

/** O que o destino exige: módulo com `ver`, valores quando declarado. Nulo = tipo desconhecido. */
export function exigenciaDoDestino(destino: DestinoDoAviso): { modulo: Modulo; valores: boolean } | null {
  // Com id, a ficha manda; sem id, a fila. A NF, que está nas duas, segue a regra.
  if (destino.id !== null && ehFicha(destino.tipo)) {
    const ficha: DestinoDeFicha = DESTINOS_DE_FICHA[destino.tipo]
    return { modulo: ficha.modulo, valores: ficha.valores === true }
  }
  if (ehFila(destino.tipo)) {
    const fila: DestinoDeFila = DESTINOS_DE_FILA[destino.tipo]
    return { modulo: fila.modulo, valores: fila.valores === true }
  }
  if (ehFicha(destino.tipo)) {
    const ficha: DestinoDeFicha = DESTINOS_DE_FICHA[destino.tipo]
    return { modulo: ficha.modulo, valores: ficha.valores === true }
  }
  return null
}

/**
 * A pessoa abre o destino do aviso? As MESMAS funções do guard (`@dv/domain`): `ver` no módulo,
 * valores quando o destino os exige e, na ficha de carteira, ver a carteira inteira ou ser o
 * dono. Tipo desconhecido não abre nada: falha fechada.
 */
export function podeAbrirODestino(nivel: NivelDeAcesso, usuarioId: string, destino: DestinoDoAviso): boolean {
  const exige = exigenciaDoDestino(destino)
  if (exige === null) return false
  if (!alcanca(nivel, exige.modulo, 'ver')) return false
  if (exige.valores && !veValoresFinanceiros(nivel, exige.modulo)) return false
  if (destino.donoId !== undefined && !veCarteiraDeTodos(nivel, exige.modulo) && destino.donoId !== usuarioId) return false
  return true
}

/**
 * O caminho relativo do destino, com a chave: `/contas-a-pagar?id=…`, `/faturamento?pedido=…`,
 * `/aprovacoes`. Nunca absoluto: quem monta o link do e-mail junta a origem do app (6.8).
 */
export function caminhoDoDestino(destino: DestinoDoAviso): string | null {
  if (destino.id !== null && ehFicha(destino.tipo)) {
    const ficha: DestinoDeFicha = DESTINOS_DE_FICHA[destino.tipo]
    if (ficha.chave === 'hash') return `${ficha.caminho}#c-${encodeURIComponent(destino.id)}`
    const partes = [
      ...(ficha.fixo === undefined ? [] : [`cat=${encodeURIComponent(ficha.fixo.cat)}`]),
      `${ficha.chave}=${encodeURIComponent(destino.id)}`,
    ]
    return `${ficha.caminho}?${partes.join('&')}`
  }
  if (ehFila(destino.tipo)) {
    const fila: DestinoDeFila = DESTINOS_DE_FILA[destino.tipo]
    if (fila.chave !== undefined && destino.id !== null) return `${fila.caminho}?${fila.chave}=${encodeURIComponent(destino.id)}`
    return fila.caminho
  }
  return null
}
